import React, { useState, useCallback, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';
import { Send, ArrowRight, CheckCircle, AlertTriangle, Loader2, Globe, Home, Clock } from 'lucide-react';
import { PinModal } from '../components/PinModal';
import { useNavigate } from 'react-router-dom';

const SendMoney = () => {
  const navigate = useNavigate();
  const [recipientEmail, setRecipientEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [transferData, setTransferData] = useState(null);
  const [recentRecipients, setRecentRecipients] = useState([]);
  const [showPin, setShowPin] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);

  useEffect(() => {
    const loadRecents = async () => {
      try {
        const res = await api.get('/transactions');
        const txs = res.data.transactions || [];
        const seen = new Set();
        const recents = [];
        for (const tx of txs) {
          if (tx.type === 'transfer_out' && tx.recipient_email && !seen.has(tx.recipient_email)) {
            seen.add(tx.recipient_email);
            recents.push(tx.recipient_email);
            if (recents.length >= 5) break;
          }
        }
        setRecentRecipients(recents);
      } catch {
        // Recent recipients are optional
      }
    };
    loadRecents();
  }, []);

  const fetchFeePreview = useCallback(async () => {
    if (!recipientEmail || !amount || parseFloat(amount) <= 0) return;
    setFeeLoading(true);
    try {
      const res = await api.post('/payments/fee-preview', {
        recipient_email: recipientEmail,
        amount: parseFloat(amount),
        description
      });
      setFeeBreakdown(res.data);
    } catch {
      setFeeBreakdown(null);
    } finally {
      setFeeLoading(false);
    }
  }, [recipientEmail, amount, description]);

  const handlePreview = async (e) => {
    e.preventDefault();
    if (!recipientEmail || !amount) {
      toast.error('Please fill in recipient and amount');
      return;
    }
    await fetchFeePreview();
    setShowConfirm(true);
  };

  const handleSend = async () => {
    // Check if user has a PIN first
    try {
      const pinCheck = await api.get('/auth/has-pin');
      if (!pinCheck.data.has_pin) {
        toast.error('Please set a transaction PIN first');
        navigate('/profile');
        return;
      }
    } catch {
      toast.error('Could not verify PIN status');
      return;
    }
    setShowPin(true);
  };

  const handlePinSubmit = async (pin) => {
    setPinLoading(true);
    try {
      const res = await api.post('/payments/send-money', {
        recipient_email: recipientEmail,
        amount: parseFloat(amount),
        description,
        transaction_pin: pin,
      });
      setTransferData(res.data);
      setShowPin(false);
      setShowConfirm(false);
      setShowSuccess(true);
      toast.success('Money sent successfully!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Transfer failed';
      toast.error(detail);
      if (detail.includes('Invalid transaction PIN')) {
        // Keep pin modal open for retry
      } else {
        setShowPin(false);
      }
    } finally {
      setPinLoading(false);
    }
  };

  const resetForm = () => {
    setRecipientEmail('');
    setAmount('');
    setDescription('');
    setFeeBreakdown(null);
    setTransferData(null);
    setShowSuccess(false);
  };

  const sym = feeBreakdown?.currency_symbol || '$';

  return (
    <div className="min-h-screen bg-muted" data-testid="send-money-page">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 py-8">
        <Card className="border border-border/50 shadow-xl" data-testid="send-money-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" />
              <CardTitle data-testid="send-money-title">Send Money</CardTitle>
            </div>
            <CardDescription>Transfer funds to any SamaFlux user. Fees apply based on routing.</CardDescription>
          </CardHeader>
          <CardContent>
            {recentRecipients.length > 0 && (
              <div className="mb-5" data-testid="quick-send-section">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-2">
                  <Clock className="h-3 w-3" /> Recent Recipients
                </p>
                <div className="flex flex-wrap gap-2">
                  {recentRecipients.map((email) => (
                    <button
                      key={email}
                      type="button"
                      onClick={() => setRecipientEmail(email)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        recipientEmail === email
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-muted/50 text-foreground hover:bg-muted border-border/50'
                      }`}
                      data-testid={`quick-send-${email}`}
                    >
                      <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                        {email[0].toUpperCase()}
                      </span>
                      {email}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <form onSubmit={handlePreview} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="recipient">Recipient Email</Label>
                <Input
                  id="recipient"
                  type="email"
                  placeholder="user@example.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  required
                  data-testid="recipient-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min="0.01"
                  step="0.01"
                  required
                  data-testid="amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Note (optional)</Label>
                <Input
                  id="description"
                  placeholder="What's this for?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="description-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full bg-primary hover:bg-primary/90 payment-button" disabled={feeLoading} data-testid="review-button">
                {feeLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculating...</> : <>Review & Send <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Fee Preview & Confirm Dialog */}
        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent data-testid="confirm-dialog">
            <DialogHeader>
              <DialogTitle data-testid="confirm-title">Confirm Transfer</DialogTitle>
              <DialogDescription>Review the fee breakdown before sending</DialogDescription>
            </DialogHeader>
            {feeBreakdown && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm mb-2" data-testid="transfer-type-badge">
                  {feeBreakdown.transfer_type === 'domestic' ? (
                    <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                      <Home className="h-3 w-3" /> Domestic Transfer
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                      <Globe className="h-3 w-3" /> International Transfer
                    </span>
                  )}
                  <span className="text-muted-foreground text-xs">
                    {feeBreakdown.sender_currency} → {feeBreakdown.recipient_currency}
                  </span>
                </div>

                <div className="bg-muted rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">To</span>
                    <span className="font-medium" data-testid="confirm-recipient">{recipientEmail}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Send Amount</span>
                    <span className="font-medium" data-testid="confirm-amount">{sym}{feeBreakdown.principal_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                  {feeBreakdown.sender_currency !== feeBreakdown.recipient_currency && (
                    <div className="flex justify-between text-sm bg-blue-50 -mx-4 px-4 py-2 rounded">
                      <span className="text-blue-700">Recipient gets</span>
                      <span className="font-semibold text-blue-700" data-testid="confirm-converted-amount">
                        {feeBreakdown.recipient_currency_symbol}{feeBreakdown.converted_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        <span className="text-xs ml-1 text-blue-500">({feeBreakdown.recipient_currency})</span>
                      </span>
                    </div>
                  )}
                  <div className="border-t border-border/50 pt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Fee ({feeBreakdown.fee_percent_rate > 0 ? `${feeBreakdown.fee_percent_rate}% + ${sym}${feeBreakdown.fee_flat_amount.toFixed(2)} flat` : `${sym}${feeBreakdown.fee_flat_amount.toFixed(2)} flat`})
                      </span>
                      <span className="font-medium text-amber-600" data-testid="confirm-fee">{sym}{feeBreakdown.total_fee.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                  <div className="border-t border-border/50 pt-2">
                    <div className="flex justify-between text-base font-semibold">
                      <span>Total Debit</span>
                      <span className="text-primary" data-testid="confirm-total">{sym}{feeBreakdown.total_debit.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                {!feeBreakdown.sufficient_balance && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg" data-testid="insufficient-warning">
                    <AlertTriangle className="h-4 w-4" />
                    Insufficient balance. You have {sym}{feeBreakdown.current_balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowConfirm(false)} className="rounded-full" data-testid="cancel-button">Cancel</Button>
              <Button
                onClick={handleSend}
                disabled={loading || !feeBreakdown?.sufficient_balance}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="confirm-send-button"
              >
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : 'Confirm & Send'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Success Dialog */}
        <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
          <DialogContent data-testid="success-dialog">
            <div className="text-center space-y-4 py-4">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h3 className="text-xl font-semibold">Money Sent!</h3>
              {transferData && (
                <div className="space-y-2 text-sm">
                  <p>You sent <strong>{sym}{transferData.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong> to <strong>{transferData.recipient}</strong></p>
                  <p className="text-muted-foreground">Fee: {sym}{transferData.fee?.toLocaleString('en-US', { minimumFractionDigits: 2 })} ({transferData.transfer_type})</p>
                  <p className="text-xs text-muted-foreground">Ref: {transferData.reference}</p>
                </div>
              )}
              <Button onClick={resetForm} className="rounded-full bg-primary hover:bg-primary/90" data-testid="send-another-button">
                Send Another
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <PinModal
          open={showPin}
          onClose={() => setShowPin(false)}
          onSubmit={handlePinSubmit}
          loading={pinLoading}
        />
      </div>
    </div>
  );
};

export default SendMoney;
